import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Plus, X, Trash2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const BLANK = { category: "materials", amount: "", description: "", incurred_on: new Date().toISOString().slice(0, 10) };

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  useDocumentTitle("Expenses");

  const load = async () => { const { data } = await api.get("/provider/expenses"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const total = items.reduce((s, i) => s + (i.amount || 0), 0);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount required"); return; }
    try {
      await api.post("/provider/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense logged");
      setForm(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/provider/expenses/${id}`); load(); } catch (_) { toast.error("Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl" data-testid="expenses-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider · Expenses</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Expenses & materials</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Track spend against bookings for accurate margins.</p>
          </div>
          <button onClick={() => setForm({ ...BLANK })} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="expense-new">
            <Plus className="h-4 w-4" /> Log expense
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-4" data-testid="expense-total">
          <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Total spend (all time)</div>
          <div className="mt-1 font-display text-2xl font-semibold text-[#071b22]">${total.toFixed(2)}</div>
        </div>

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/60">No expenses logged yet.</div>
        ) : (
          <ul className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="expense-list">
            {items.map((e) => (
              <li key={e.id} className="p-4 flex items-center gap-3" data-testid={`expense-row-${e.id}`}>
                <Receipt className="h-4 w-4 text-[#165a71]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#071b22]"><b>{e.category}</b> · {e.description || "—"}</div>
                  <div className="text-xs text-[#252A34]/60">{e.incurred_on || new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                <div className="font-medium text-[#071b22]">${(e.amount || 0).toFixed(2)}</div>
                <button onClick={() => remove(e.id)} className="text-xs text-rose-600 ml-3" data-testid={`expense-delete-${e.id}`}><Trash2 className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}

        {form && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-6 border border-slate-200" data-testid="expense-form">
              <div className="flex items-start justify-between">
                <div className="font-display text-lg font-semibold text-[#071b22]">Log expense</div>
                <button onClick={() => setForm(null)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="expense-category">
                  {["materials", "travel", "equipment", "software", "other"].map((c) => <option key={c}>{c}</option>)}
                </select>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Amount" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="expense-amount" />
                <input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Description" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="expense-desc" />
                <input type="date" value={form.incurred_on} onChange={(e) => setForm((p) => ({ ...p, incurred_on: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="expense-date" />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setForm(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="expense-submit">Log</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
